import { getSessionUser } from "@/lib/supabase/auth";
import { getSupabaseClient } from "@/lib/supabase/client";

export type RitualSessionSummary = {
  id: string;
  circleId: string;
  circleName: string;
  circleActivity: string | null;
  title: string;
  description: string;
  hostName: string;
  lat: number;
  lng: number;
  createdAt: string;
  active: boolean;
  memberCount: number;
  memberPreview: Array<{
    id: string;
    displayName: string;
    photoUrl: string | null;
  }>;
};

function makeId(prefix: string) {
  try {
    const uid = globalThis.crypto?.randomUUID?.();
    if (uid) return `${prefix}_${uid}`;
  } catch {
    // ignore
  }
  return `${prefix}_${Math.random().toString(36).slice(2, 12)}`;
}

function formatDisplayNameFromHandle(value: unknown) {
  return String(value ?? "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (ch) => ch.toLocaleUpperCase("tr-TR"));
}

function isGenericDisplayName(value: unknown) {
  const normalized = String(value ?? "")
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, " ")
    .trim();

  if (!normalized) return true;
  if (["kullanıcı", "kullanici", "user", "biri", "üye", "uye"].includes(normalized)) return true;
  return normalized === "bir fincan sevgi" || normalized.startsWith("bir fincan sevgi ");
}

function resolveDisplayName(displayName: unknown, handle: unknown, fallback: string) {
  const cleanDisplayName = String(displayName ?? "").trim();
  if (!isGenericDisplayName(cleanDisplayName)) return cleanDisplayName;
  const normalizedHandle = formatDisplayNameFromHandle(handle);
  return normalizedHandle || cleanDisplayName || fallback;
}

export async function listNearbyRitualSessions(args: {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
  limit?: number;
}) {
  const supabase = getSupabaseClient();
  const { minLat, maxLat, minLng, maxLng } = args;
  const limit = Math.max(1, Math.min(200, Math.trunc(args.limit ?? 120)));

  const { data, error } = await supabase
    .from("ritual_sessions")
    .select("id, circle_id, host_user_id, title, description, lat, lng, created_at, active")
    .eq("active", true)
    .gte("lat", minLat)
    .lte("lat", maxLat)
    .gte("lng", minLng)
    .lte("lng", maxLng)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) throw error;

  const rows = (data ?? []) as any[];

  const circleIds = Array.from(new Set(rows.map((row) => String(row.circle_id ?? "")).filter(Boolean)));
  const hostUserIds = Array.from(new Set(rows.map((row) => String(row.host_user_id ?? "")).filter(Boolean)));

  const [circlesRes, profilesRes, membersRes] = await Promise.all([
    circleIds.length
      ? supabase.from("circles").select("id, name, activity").in("id", circleIds)
      : Promise.resolve({ data: [], error: null } as any),
    hostUserIds.length
      ? supabase.from("karma_profiles").select("id, display_name, handle").in("id", hostUserIds)
      : Promise.resolve({ data: [], error: null } as any),
    circleIds.length
      ? supabase.from("circle_members").select("circle_id, user_id, created_at").in("circle_id", circleIds).order("created_at", { ascending: true })
      : Promise.resolve({ data: [], error: null } as any),
  ]);

  if (circlesRes.error) throw circlesRes.error;
  if (profilesRes.error) throw profilesRes.error;
  if (membersRes.error) throw membersRes.error;

  const circleMetaById = new Map<string, { name: string; activity: string | null }>();
  for (const row of (circlesRes.data ?? []) as any[]) {
    const id = String(row?.id ?? "");
    if (!id) continue;
    const activity = typeof row?.activity === "string" ? row.activity.trim() : "";
    circleMetaById.set(id, { name: String(row?.name ?? "Çember"), activity: activity || null });
  }

  const hostNameById = new Map<string, string>();
  for (const row of (profilesRes.data ?? []) as any[]) {
    const id = String(row?.id ?? "");
    if (!id) continue;
    const displayName = typeof row?.display_name === "string" ? row.display_name.trim() : "";
    const handle = typeof row?.handle === "string" ? row.handle.trim() : "";
    hostNameById.set(id, resolveDisplayName(displayName, handle, "Üye"));
  }

  const memberRows = (membersRes.data ?? []) as any[];
  const memberUserIds = Array.from(new Set(memberRows.map((row) => String(row?.user_id ?? "")).filter(Boolean)));

  const memberProfilesRes = memberUserIds.length
    ? await supabase.from("karma_profiles").select("id, display_name, handle, photo_url").in("id", memberUserIds)
    : ({ data: [], error: null } as any);

  if (memberProfilesRes.error) throw memberProfilesRes.error;

  const memberProfileById = new Map<string, { displayName: string; photoUrl: string | null }>();
  for (const row of (memberProfilesRes.data ?? []) as any[]) {
    const id = String(row?.id ?? "");
    if (!id) continue;
    const displayName = resolveDisplayName(row?.display_name, row?.handle, "Üye");
    const photoUrl = typeof row?.photo_url === "string" && row.photo_url.trim() ? row.photo_url.trim() : null;
    memberProfileById.set(id, { displayName, photoUrl });
  }

  const membersByCircle = new Map<string, Array<{ id: string; displayName: string; photoUrl: string | null }>>();
  for (const row of memberRows) {
    const circleId = String(row?.circle_id ?? "");
    const userId = String(row?.user_id ?? "");
    if (!circleId || !userId) continue;
    const profile = memberProfileById.get(userId);
    const existing = membersByCircle.get(circleId) ?? [];
    existing.push({
      id: userId,
      displayName: profile?.displayName || "Üye",
      photoUrl: profile?.photoUrl ?? null,
    });
    membersByCircle.set(circleId, existing);
  }

  return rows
    .map((row) => {
      const id = String(row?.id ?? "");
      const circleId = String(row?.circle_id ?? "");
      const lat = Number(row?.lat ?? 0);
      const lng = Number(row?.lng ?? 0);
      if (!id || !circleId || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;

      const members = membersByCircle.get(circleId) ?? [];
      const circleMeta = circleMetaById.get(circleId);

      return {
        id,
        circleId,
        circleName: circleMeta?.name || "Çember",
        circleActivity: circleMeta?.activity ?? null,
        title: String(row?.title ?? "Ritüel"),
        description: typeof row?.description === "string" ? row.description : "",
        hostName: hostNameById.get(String(row?.host_user_id ?? "")) || "Üye",
        lat,
        lng,
        createdAt: String(row?.created_at ?? new Date().toISOString()),
        active: Boolean(row?.active),
        memberCount: members.length,
        memberPreview: members.slice(0, 3),
      } satisfies RitualSessionSummary;
    })
    .filter(Boolean) as RitualSessionSummary[];
}

export async function createRitualSessionFromCircle(args: {
  circleId: string;
  title: string;
  description?: string;
  lat: number;
  lng: number;
}) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Ritüel paylaşmak için giriş yapmalısın.");

  const title = String(args.title ?? "").trim().slice(0, 120);
  if (!title) throw new Error("Ritüel başlığı gerekli.");

  const description = typeof args.description === "string" ? args.description.trim().slice(0, 400) : "";
  const lat = Number(args.lat);
  const lng = Number(args.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error("Konum bilgisi geçersiz.");

  const supabase = getSupabaseClient();
  const payload = {
    id: makeId("ritual"),
    circle_id: args.circleId,
    host_user_id: sessionUser.id,
    title,
    description: description || null,
    lat,
    lng,
    active: true,
  };

  const { data, error } = await supabase
    .from("ritual_sessions")
    .insert(payload as any)
    .select("id, circle_id, host_user_id, title, description, lat, lng, created_at, active")
    .single();

  if (error) throw error;

  const [circleRes, profileRes] = await Promise.all([
    supabase.from("circles").select("name").eq("id", args.circleId).maybeSingle(),
    supabase.from("karma_profiles").select("display_name, handle").eq("id", sessionUser.id).maybeSingle(),
  ]);

  const circleName = String(circleRes.data?.name ?? "Çember");
  const hostName =
    (typeof profileRes.data?.display_name === "string" && profileRes.data.display_name.trim()) ||
    (typeof profileRes.data?.handle === "string" && profileRes.data.handle.trim()) ||
    "Üye";

  return {
    id: String(data.id),
    circleId: String(data.circle_id),
    circleName,
    circleActivity: null,
    title: String(data.title),
    description: typeof data.description === "string" ? data.description : "",
    hostName,
    lat: Number(data.lat),
    lng: Number(data.lng),
    createdAt: String(data.created_at),
    active: Boolean(data.active),
    memberCount: 0,
    memberPreview: [],
  } satisfies RitualSessionSummary;
}

export async function upsertMyRitualSessionForCircle(args: {
  circleId: string;
  title: string;
  description?: string;
  lat: number;
  lng: number;
}) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Ritüel paylaşmak için giriş yapmalısın.");

  const title = String(args.title ?? "").trim().slice(0, 120);
  if (!title) throw new Error("Ritüel başlığı gerekli.");

  const description = typeof args.description === "string" ? args.description.trim().slice(0, 400) : "";
  const lat = Number(args.lat);
  const lng = Number(args.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error("Konum bilgisi geçersiz.");

  const supabase = getSupabaseClient();
  const existingRes = await supabase
    .from("ritual_sessions")
    .select("id")
    .eq("circle_id", args.circleId)
    .eq("host_user_id", sessionUser.id)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (existingRes.error) throw existingRes.error;

  if (existingRes.data?.id) {
    const { error } = await supabase
      .from("ritual_sessions")
      .update({
        title,
        description: description || null,
        lat,
        lng,
        active: true,
      } as any)
      .eq("id", String(existingRes.data.id));

    if (error) throw error;
    return String(existingRes.data.id);
  }

  const insertRes = await supabase.from("ritual_sessions").insert({
    id: makeId("ritual"),
    circle_id: args.circleId,
    host_user_id: sessionUser.id,
    title,
    description: description || null,
    lat,
    lng,
    active: true,
  } as any);

  if (insertRes.error) throw insertRes.error;
  return null;
}

export async function deactivateMyRitualSessionForCircle(circleId: string) {
  const sessionUser = await getSessionUser();
  if (!sessionUser?.id) throw new Error("Oturum yok.");

  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from("ritual_sessions")
    .update({ active: false } as any)
    .eq("circle_id", circleId)
    .eq("host_user_id", sessionUser.id)
    .eq("active", true);

  if (error) throw error;
}

export function subscribeNearbyRitualSessions(onChange: () => void) {
  const supabase = getSupabaseClient();

  const channel = supabase
    .channel(`ritual_sessions:nearby:${Math.random().toString(36).slice(2, 10)}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "ritual_sessions" }, () => {
      onChange();
    })
    .subscribe();

  return () => {
    void supabase.removeChannel(channel);
  };
}
